import React, { useEffect, useRef, useState } from 'react';

// Validation libraries
import { useFormik } from 'formik';
import * as yup from 'yup';

//Hooks
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// Actions
import { failure, success } from '~/redux/actions/snackbar-actions.js';
import { fetchContactLabels, updateContact } from '~/redux/actions/contacts-actions.js';

// Utils
import request from '~/utils/request.js';
import { EMAIL_REGEX } from '~/utils/constants.js';
import { MESSAGES } from '~/utils/message.js';
import { validateForm, resetValidation } from '~/utils/formik-validation.js';
import { handlePhoneNumberChange } from '~/utils/helperFunctions';

// Components
import MultiSelect from '~/components/General/MultiSelectNew/index.jsx';
import CreateTagModal from '~/components/Settings/Tags/CreateTag/index.jsx';
import CustomFields, { getCustomFieldUrlErrors } from '~/components/Contacts/UpdateContactsNew/CustomFields/index.jsx';
import EventLog from '~/components/Contacts/UpdateContactsNew/EventLog/index.jsx';
import MailingTab from '~/components/Contacts/MailingTab';
import ContactDetailsSkeleton from '~/components/Contacts/ContactDetailsSkeleton';
import Notes from '~/components/Contacts/Notes';
import PropertyTab from '~/components/Contacts/PropertyTab';
import ContactsMeta from '~/components/Contacts/ContactsMeta';
import Engagement from '~/components/Contacts/ContactDetails/Engagement';
import OlcButton from '~/components/General/OlcButton';

// MUI Components
import {
  Box,
  Grid,
  Typography,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Tabs,
  Tab,
  TextField,
  FormControlLabel,
  Checkbox,
  Tooltip,
  InputAdornment,
  IconButton,
} from '@mui/material';

// MUI Icons
import { ContactInfoCIcon, PlusIconCIcon } from '~/assets/images';
import HighlightOffIcon from '@mui/icons-material/HighlightOffOutlined';
import CloseIcon from '@mui/icons-material/Close';

// Styles
import './styles.scss';

const UpdateContact = (props) => {
  const {
    propertyAddress,
    setPropertyAddress,
    customFields,
    setCustomFields,
    isPropertyAddressEnabled,
    handleSaveSuccess,
    fetchContactLogs,
    modifiedContactsFields,
    logs,
    eventType,
    setEventType,
    logLoading,
    mainLoader,
    updatePagination,
    isEditing,
    updateCustomFields,
    setDetailsActiveTab,
  } = props;
  const [contact, setContact] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTags, setSelectedTags] = useState([]);
  const [createTagModal, setCreateTagModal] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [isDncList, setIsDncList] = useState(false);

  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const childRef = useRef();

  const tags = useSelector((state) => state.contactsReducers.labels);
  const allCustomFields = useSelector((state) => state?.customFieldsReducers?.allCustomFields);
  const customFieldUrlErrors = getCustomFieldUrlErrors(customFields, allCustomFields);
  const hasCustomFieldUrlErrors = Object.keys(customFieldUrlErrors).length > 0;

  // Sort the tags alphabetically
  const sortedTags = [...tags].sort((a, b) => a?.title.localeCompare(b?.title));

  const tagsOptions = sortedTags
    ?.map((tag) => {
      return {
        id: tag.id,
        title: tag.title,
      };
    })
    .concat({
      id: '-1',
      title: 'Create New Tag',
      custom: true,
      icon: null,
      func: () => {
        setCreateTagModal(true);
      },
    });

  const contactId = params.id;

  const validationSchema = yup.object().shape({
    firstName: yup
      .string()
      .test({
        test: function (value) {
          const { companyName } = this.parent;
          return !!value || !!companyName;
        },
        message: MESSAGES.CONTACTS.EDIT.FIRST_NAME_OR_COMPANY,
      })
      .max(30, MESSAGES.ACCOUNT.PROFILE.EDIT_NAME.FIRST_NAME_LIMIT)
      .notRequired(),
    companyName: yup
      .string()
      .max(100, MESSAGES.CONTACTS.EDIT.COMPANY_MAX_LENGTH)
      .test({
        test: function (value) {
          const { firstName } = this.parent;
          return !!value || !!firstName;
        },
        message: MESSAGES.CONTACTS.EDIT.FIRST_NAME_OR_COMPANY,
      })
      .notRequired(),
    lastName: yup
      .string()
      .max(30, MESSAGES.ACCOUNT.PROFILE.EDIT_NAME.LAST_NAME_LIMIT)
      .notRequired(),
    phoneNo: yup
      .string()
      .max(20, MESSAGES.ACCOUNT.PROFILE.EDIT_ACCOUNT_INFO.PHONE_LIMIT)
      .nullable(true),
    email: yup.string().matches(EMAIL_REGEX, MESSAGES.AUTH.INVALID_EMAIL).notRequired(),
    isDncList: yup.boolean().notRequired(),
  });

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      companyName: '',
      phoneNo: '',
      email: '',
      isDncList: false,
    },
    validationSchema: validationSchema,
  });

  const removeTag = (tag) => {
    setSelectedTags((selectedTags) =>
      selectedTags.filter((item) => {
        return item !== tag;
      })
    );
  };

  const fetchContactData = async () => {
    try {
      setIsLoading(true);
      const response = await request.get(`contacts/${contactId}`, {});
      if (response.status === 200) {
        setContact(response.data.data);
      }
    } catch (error) {
      navigate('/contacts');
    } finally {
      setIsLoading(false);
    }
  };

  const onUpdateContact = async (event) => {
    try {
      event.preventDefault();
      if (formik.values.firstName && /^[a-zA-Z ]*$/.test(formik.values.firstName)) {
        delete formik.errors.firstName;
      }

      if (formik.values.companyName && formik.values.companyName.length < 100) {
        delete formik.errors.companyName;
      }

      if (!formik.values.companyName) {
        delete formik.errors.companyName;
      }

      const isValid = validateForm(formik);
      if (!isValid) {
        return;
      }
      if (hasCustomFieldUrlErrors) {
        setActiveTab(2);
        setDetailsActiveTab(2);
        return;
      }
      const payload = formik.values;
      payload.id = contactId;
      selectedTags.length ? (payload.tags = selectedTags) : undefined;
      payload.email = payload?.email?.length ? payload?.email : null;
      payload.isDncList = isDncList;
      if (isPropertyAddressEnabled) {
        payload.propertyAddress = propertyAddress?.address;
        payload.propertyCity = propertyAddress?.city;
        payload.propertyState = propertyAddress?.state;
        payload.propertyZip = propertyAddress?.zip;
      }
      setIsLoading(true);
      setActiveTab(0);
      setDetailsActiveTab(0);
      const response = await dispatch(updateContact(payload));
      if (response.status === 200) {
        const customFieldsSaved = await updateCustomFields();
        if (!customFieldsSaved) {
          setIsLoading(false);
          return;
        }
        fetchContactLogs();
        handleSaveSuccess();
        dispatch(success(response.data.message));
      } else {
        dispatch(failure(response?.data?.data?.errors[0]?.msg || response?.data?.message));
      }
    } catch (error) {
      dispatch(failure(error.message));
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 3000);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    if (Object.keys(contact).length) {
      Object.keys(formik.initialValues).forEach((fieldName) => {
        formik.setFieldValue(fieldName, contact[fieldName]);
        formik.setFieldTouched(fieldName, false);
        formik.touched[fieldName] = false;
      });
      if (contact.ContactLabels?.length) {
        setSelectedTags(() => [...contact.ContactLabels.map((tag) => tag.title)]);
      }
      if (contact.dncMarkedAt) {
        setIsDncList(true);
      } else {
        setIsDncList(false);
      }
    }
  }, [contact]);

  useEffect(() => {
    resetValidation();
    formik.handleReset();
    Object.keys(formik.initialValues).forEach((fieldName) => {
      formik.setFieldTouched(fieldName, false);
      formik.touched[fieldName] = false;
    });
    fetchContactData();
    dispatch(fetchContactLabels());
  }, []);

  return (
    <Grid container className="detailsGrid">
      <Box className="detailsHeader editDetailsHeader">
        <Box className="contactDetailsAction">
          <OlcButton variant="secondary" onClick={handleSaveSuccess}>
            Cancel
          </OlcButton>
          <OlcButton
            variant="primary"
            onClick={onUpdateContact}
            disabled={isLoading || hasCustomFieldUrlErrors}
          >
            {isLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Save Changes'}
          </OlcButton>
        </Box>
      </Box>
      <Grid container columnSpacing={4} rowSpacing={4} className="detailsColumns">
        <Grid item lg={8} md={12} sm={12} xs={12} className="detailsMainColumn">
          <Card className="detailsCard">
            <CardContent>
              <Box className="detailsCardHeader">
                <Typography className="contactDetailsTitle">
                  {MESSAGES.CONTACTS.CONTACT_DETAIL_TITLE}
                </Typography>
              </Box>
              {isLoading ? (
                <ContactDetailsSkeleton variant="edit-form" />
              ) : (
                <form onSubmit={onUpdateContact}>
                  <Box className="editFormLayout">
                    {/* First Row: First Name and Last Name */}
                    <Box className="editFormRow">
                      <Box className="editFormColumn">
                        <Typography className="editFieldLabel">First Name</Typography>
                        <TextField
                          fullWidth
                          name="firstName"
                          value={formik.values.firstName || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                          helperText={formik.touched.firstName && formik.errors.firstName}
                          variant="outlined"
                          className="editTextField"
                          placeholder="First Name"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment
                                position="end"
                                sx={{ width: '16px', marginRight: '8px' }}
                              >
                                {formik.values.firstName && (
                                  <IconButton
                                    className="cpClearInputBtn smallClearIcon"
                                    size="small"
                                    onClick={() => formik.setFieldValue('firstName', '')}
                                    edge="end"
                                    sx={{ right: '15px' }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                      <Box className="editFormColumn">
                        <Typography className="editFieldLabel">Last Name</Typography>
                        <TextField
                          fullWidth
                          name="lastName"
                          value={formik.values.lastName || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                          helperText={formik.touched.lastName && formik.errors.lastName}
                          variant="outlined"
                          className="editTextField"
                          placeholder="Last Name"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment
                                position="end"
                                sx={{ width: '16px', marginRight: '8px' }}
                              >
                                {formik.values.lastName && (
                                  <IconButton
                                    className="cpClearInputBtn smallClearIcon"
                                    size="small"
                                    onClick={() => formik.setFieldValue('lastName', '')}
                                    edge="end"
                                    sx={{ right: '15px' }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Second Row: Phone Number and Email */}
                    <Box className="editFormRow">
                      <Box className="editFormColumn">
                        <Typography className="editFieldLabel">Company</Typography>
                        <TextField
                          fullWidth
                          name="companyName"
                          value={formik.values.companyName || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.companyName && Boolean(formik.errors.companyName)}
                          helperText={formik.touched.companyName && formik.errors.companyName}
                          variant="outlined"
                          className="editTextField"
                          placeholder="Company"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment
                                position="end"
                                sx={{ width: '16px', marginRight: '8px' }}
                              >
                                {formik.values.companyName && (
                                  <IconButton
                                    className="cpClearInputBtn smallClearIcon"
                                    size="small"
                                    onClick={() => formik.setFieldValue('companyName', '')}
                                    edge="end"
                                    sx={{ right: '15px' }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                      <Box className="editFormColumn">
                        <Typography className="editFieldLabel">Contact Phone Number</Typography>
                        <TextField
                          fullWidth
                          name="phoneNo"
                          value={formik.values.phoneNo || ''}
                          onChange={(e) => {
                            handlePhoneNumberChange(
                              e.target.value,
                              formik.setFieldValue,
                              'phoneNo'
                            );
                          }}
                          onBlur={formik.handleBlur}
                          error={formik.touched.phoneNo && Boolean(formik.errors.phoneNo)}
                          helperText={formik.touched.phoneNo && formik.errors.phoneNo}
                          variant="outlined"
                          className="editTextField"
                          placeholder="(123) 456-7890"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment
                                position="end"
                                sx={{ width: '16px', marginRight: '8px' }}
                              >
                                {formik.values.phoneNo && (
                                  <IconButton
                                    className="cpClearInputBtn smallClearIcon"
                                    size="small"
                                    onClick={() => formik.setFieldValue('phoneNo', '')}
                                    edge="end"
                                    sx={{ right: '15px' }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Third Row: Company and Tags */}
                    <Box className="editFormRow">
                      <Box className="editFormColumn">
                        <Typography className="editFieldLabel">Contact Email</Typography>
                        <TextField
                          fullWidth
                          name="email"
                          value={formik.values.email || ''}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.email && Boolean(formik.errors.email)}
                          helperText={formik.touched.email && formik.errors.email}
                          variant="outlined"
                          className="editTextField"
                          placeholder="Contact Email"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment
                                position="end"
                                sx={{ width: '16px', marginRight: '8px' }}
                              >
                                {formik.values.email && (
                                  <IconButton
                                    className="cpClearInputBtn smallClearIcon"
                                    size="small"
                                    onClick={() => formik.setFieldValue('email', '')}
                                    edge="end"
                                    sx={{ right: '15px' }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                      <Box className="editFormColumn tagsColumn">
                        <Typography className="editFieldLabel">Tags</Typography>
                        <MultiSelect
                          component={'tag-create'}
                          options={tagsOptions}
                          selectedValue={selectedTags}
                          setSelectedValue={setSelectedTags}
                          placeHolderText={
                            MESSAGES.CONTACTS.ADVANCE_FILTERS.TAG_SEARCH_PLACEHOLDER_NEW
                          }
                          enabledSearch={true}
                          searchAutocomplete={true}
                          customIcon={true}
                          clearable={true}
                        />
                      </Box>
                    </Box>
                  </Box>
                </form>
              )}
            </CardContent>
          </Card>

          {/* Tabs and additional content remain the same */}
          <Box className="tabsContainer">
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              className="contactTabs"
              TabIndicatorProps={{
                className: 'tabIndicator',
              }}
            >
              <Tab
                label={
                  <Box className="tabLabel">
                    <>Mailing Address</>
                    <Tooltip
                      title={MESSAGES.CONTACTS.MAILING_ADDRESS_TOOLTIP}
                      placement="bottom-start"
                      componentsProps={{
                        tooltip: {
                          sx: {
                            fontSize: '12px',
                            maxWidth: '196px',
                            padding: '8px !important',
                          },
                        },
                      }}
                    >
                      <img
                        src={ContactInfoCIcon}
                        alt="Mailing Address"
                        style={{ cursor: 'pointer' }}
                      />
                    </Tooltip>
                  </Box>
                }
                className="contactTab"
              />
              <Tab
                label={
                  <Box className="tabLabel">
                    <>Property Address</>
                    <Tooltip
                      title={MESSAGES.CONTACTS.PROPERTY_ADDRESS_TOOLTIP}
                      placement="bottom-start"
                      componentsProps={{
                        tooltip: {
                          sx: {
                            fontSize: '12px',
                            maxWidth: '182px',
                            padding: '8px !important',
                          },
                        },
                      }}
                    >
                      <img
                        src={ContactInfoCIcon}
                        alt="Property Address"
                        style={{ cursor: 'pointer' }}
                      />
                    </Tooltip>
                  </Box>
                }
                className="contactTab"
              />
              <Tab label="Custom Fields" className="contactTab" />
              {/* <Tab label="Engagement" className="contactTab" /> */}
              <Tab label="Event Logs" className="contactTab" />
            </Tabs>
          </Box>
          <Card className="tabbedCard">
            <CardContent className="tabContent">
              {/* Mailing Tab Content */}
              {activeTab === 0 &&
                (isLoading ? (
                  <ContactDetailsSkeleton variant="mailing-tab" />
                ) : (
                  <MailingTab orignalContactData={contact} />
                ))}
              {/* Property Tab Content (EDIT MODE) */}
              {activeTab === 1 &&
                (isLoading ? (
                  <ContactDetailsSkeleton variant="property-tab" />
                ) : (
                  <PropertyTab
                    propertyAddress={propertyAddress}
                    isEditing={isEditing}
                    setPropertyAddress={setPropertyAddress}
                    onUpdateContact={onUpdateContact}
                  />
                ))}
              {/* Custom Fields Tab Content */}
              {activeTab === 2 &&
                (isLoading ? (
                  <ContactDetailsSkeleton variant="custom-fields" />
                ) : (
                  <CustomFields
                    isEditing={isEditing}
                    customFields={customFields}
                    setCustomFields={setCustomFields}
                  />
                ))}
              {/* Engagement Tab Content */}
              {/* {activeTab === 3 && (
                <Engagement />
              )} */}
              {/* Event Logs Tab Content */}
              {activeTab === 3 && (
                <EventLog
                  data={logs}
                  eventType={eventType}
                  setEventType={setEventType}
                  logLoading={logLoading}
                  mainLoader={mainLoader || isLoading}
                  updatePagination={updatePagination}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item lg={4} md={12} sm={12} xs={12} className="detailsSidebarColumn">
          <Box className="detailsSidebar">
            {/* Contacts Meta */}
            <ContactsMeta
              isLoading={isLoading}
              orignalContactData={contact}
              isEditing={isEditing}
              isDncList={isDncList}
              setIsDncList={setIsDncList}
            />

            {/* Notes */}
            <Notes isLoading={isLoading} contactId={contactId} />
          </Box>
        </Grid>
      </Grid>
      <CreateTagModal
        openModal={createTagModal}
        handleCloseModal={() => {
          setCreateTagModal(false);
          // Refresh contact labels to include newly created tags
          dispatch(fetchContactLabels());
        }}
        setSelectedTags={setSelectedTags}
        setTags={true}
      />
    </Grid>
  );
};

export default UpdateContact;
